import {
  Component,
  input,
  signal,
  computed,
  ViewChild,
  ElementRef,
  afterNextRender,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';

type TooltipPosition = 'above' | 'below' | 'left' | 'right';

/**
 * Text truncation component with conditional tooltip
 *
 * Automatically detects text overflow and enables tooltip only when text is truncated.
 *
 * @example
 * <app-truncated-text
 *   [text]="'Very long text that will be truncated...'"
 *   [maxLines]="2"
 *   [maxWidth]="'200px'"
 * />
 */
@Component({
  selector: 'app-truncated-text',
  imports: [MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './truncated-text.component.html',
  styleUrl: './truncated-text.component.scss',
})
export class TruncatedTextComponent {
  @ViewChild('textElement', { read: ElementRef, static: false })
  private readonly textElement?: ElementRef<HTMLSpanElement>;

  text = input.required<string>();
  maxLines = input<number>(1);

  /** @example '200px', '50%', '20rem' */
  maxWidth = input<string | undefined>(undefined);
  tooltipPosition = input<TooltipPosition>('above');
  tooltipDelay = input<number>(100);
  customClass = input<string>('');

  protected isTruncated = signal<boolean>(false);

  protected isEmpty = computed(() => !this.text() || this.text().trim().length === 0);

  constructor() {
    afterNextRender(() => {
      this.checkOverflow();
    });
  }

  private checkOverflow(): void {
    if (!this.textElement) return;

    const element = this.textElement.nativeElement;

    // Wait for next frame to ensure accurate dimensions
    requestAnimationFrame(() => {
      const isOverflowing =
        element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;

      this.isTruncated.set(isOverflowing);
    });
  }
}
